import cx from 'classnames';
import React, {useContext, useMemo} from 'react';
import {useIsLargishAndUp} from '~/common/hooks/useMatchQuery';
import {BottomSheetTabContext} from '~/QuickEcards/context/BottomSheetTabContext';
import {useCurrentRoute} from '~/QuickEcards/hooks/useCurrentRoute';
import {useNavigateToRoute} from '~/QuickEcards/hooks/useNavigateToRoute';
import {useAppStore} from '~/QuickEcards/store';
import {Button} from '~library/atoms/Button';
import {Typography} from '~library/atoms/Typography';
import {CardDesignIcon} from '~library/svg/CardDesignIcon';
import {DiagonalLinesIcon} from '~library/svg/DiagonalLinesIcon';
import {RevealIcon} from '~library/svg/RevealIcon';

import './OptionsPanel.sass';

const Stamp = ({
  activeTab,
  icon,
  title,
  handleStampClick,
}: {
  activeTab: boolean;
  icon: React.ReactNode;
  title: string;
  handleStampClick: () => void;
}) => (
  <div className="options-panel__stamp">
    <Button
      variant="secondary"
      size="small"
      onClick={handleStampClick}
      data-qa-id={title.toLowerCase().replaceAll(' ', '-')}
      className={cx('options-panel__stamp__icon', {selected: activeTab})}
    >
      {icon}
    </Button>
    <Typography size="smaller" variant="paragraph4" color={activeTab ? 'charcoal' : 'pebble'}>
      {title}
    </Typography>
  </div>
);

export const OptionsPanel = React.forwardRef<HTMLDivElement>((_, ref) => {
  const store = useAppStore();
  const {currentRoute} = useCurrentRoute();
  const navigateToRoute = useNavigateToRoute();
  const {tab, handleStampClick} = useContext(BottomSheetTabContext);
  const isLargishAndUp = useIsLargishAndUp();

  const nextButtonText = useMemo(
    () =>
      typeof currentRoute?.nextButtonText === 'function'
        ? currentRoute.nextButtonText(store)
        : currentRoute?.nextButtonText,
    [currentRoute, store]
  );
  const nextButtonVariant = useMemo(
    () =>
      typeof currentRoute?.nextButtonVariant === 'function'
        ? currentRoute.nextButtonVariant(store)
        : currentRoute?.nextButtonVariant,
    [currentRoute, store]
  );

  return (
    <div className="options-panel" ref={ref}>
      <div className="options-panel__stamps-container">
        <Stamp
          icon={<CardDesignIcon />}
          title="Design Gallery"
          handleStampClick={() => handleStampClick('design')}
          activeTab={tab === 'design'}
        />
        <Stamp
          icon={<RevealIcon />}
          title="Reveals"
          handleStampClick={() => handleStampClick('reveals')}
          activeTab={tab === 'reveals'}
        />
        <Stamp
          icon={<DiagonalLinesIcon />}
          title="Effects"
          handleStampClick={() => handleStampClick('effects')}
          activeTab={tab === 'effects'}
        />
      </div>
      <Button
        className={cx({'expand-next-button': isLargishAndUp})}
        fullWidth={isLargishAndUp}
        onClick={() => navigateToRoute('next', store)}
        data-qa-id="nav-bottom-next"
        size="large"
        variant={nextButtonVariant}
      >
        {nextButtonText}
      </Button>
    </div>
  );
});
